import React, { useState, useCallback } from "react";
import useAxios from "../../../../utility/axios-token-manager/init";
import Flash from "../../../../utility/Flash";
import { loadStart, loadStop } from "../../../../redux/actions/loading";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

const PRIMARY = "#a7cc48";
const DARK = "#2c2d3f";
const ERROR = "#e53935";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\d{10,11}$/;
const RELATIONSHIPS = ["Brother", "Daughter", "Father", "Husband", "Mother", "Sister", "Son", "Wife", "Other"].sort();

function passwordStrength(pwd) {
  if (!pwd) return 0;
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  return score;
}

const strengthLabel = ["", "Weak", "Fair", "Good", "Strong", "Very strong"];
const strengthColor = ["", "#e53935", "#ff7043", "#ffa726", PRIMARY, "#43a047"];

function validate(name, value, inputs) {
  switch (name) {
    case "fullName":
      return value.trim().length < 2 ? "Enter your full name" : "";
    case "email":
      return !EMAIL_RE.test(value.trim()) ? "Enter a valid email address" : "";
    case "mobile":
      return !PHONE_RE.test(value.trim()) ? "Enter a valid 10 or 11-digit mobile number" : "";
    case "address":
      return value.trim().length < 4 ? "Enter your residential address" : "";
    case "nok":
      return value.trim().length < 2 ? "Enter your next of kin name" : "";
    case "nokRelationship":
      return !value ? "Select a relationship" : "";
    case "nokAddress":
      return value.trim().length < 4 ? "Enter next of kin address" : "";
    case "nokPhone":
      return !PHONE_RE.test(value.trim()) ? "Enter a valid 10 or 11-digit phone number" : "";
    case "password":
      return value.length < 8 ? "Password must be at least 8 characters" : "";
    case "password2":
      return value !== inputs.password ? "Passwords do not match" : "";
    default:
      return "";
  }
}

function Field({ label, name, type = "text", value, error, touched, onChange, onBlur, hint, maxLength, autoComplete, children, required = true }) {
  return (
    <div style={s.fieldWrap}>
      <label style={s.label}>
        {label}
        {required && <span style={s.required}>*</span>}
      </label>
      {children || (
        <input
          style={{
            ...s.input,
            ...(touched && error ? s.inputError : {}),
            ...(touched && !error && value ? s.inputOk : {}),
          }}
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          autoComplete={autoComplete || "off"}
          maxLength={maxLength}
        />
      )}
      {touched && error && <span style={s.errorMsg}><i className="bi bi-exclamation-circle" style={{ marginRight: 4 }} />{error}</span>}
      {hint && !error && <span style={s.hint}>{hint}</span>}
    </div>
  );
}

function PasswordField({ label, name, value, error, touched, onChange, onBlur }) {
  const [show, setShow] = useState(false);
  return (
    <div style={s.fieldWrap}>
      <label style={s.label}>{label}<span style={s.required}>*</span></label>
      <div style={s.pwdRow}>
        <input
          style={{
            ...s.input,
            paddingRight: 44,
            ...(touched && error ? s.inputError : {}),
            ...(touched && !error && value ? s.inputOk : {}),
          }}
          type={show ? "text" : "password"}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          autoComplete="new-password"
        />
        <button type="button" style={s.eyeBtn} onClick={() => setShow(p => !p)}>
          <i className={show ? "bi bi-eye-slash" : "bi bi-eye"} />
        </button>
      </div>
      {touched && error && <span style={s.errorMsg}><i className="bi bi-exclamation-circle" style={{ marginRight: 4 }} />{error}</span>}
    </div>
  );
}

function SectionTitle({ icon, title, subtitle }) {
  return (
    <div style={s.sectionTitle}>
      <div style={s.sectionIcon}><i className={icon} style={{ fontSize: 16, color: PRIMARY }} /></div>
      <div>
        <div style={s.sectionTitleText}>{title}</div>
        {subtitle && <div style={s.sectionSubtitle}>{subtitle}</div>}
      </div>
    </div>
  );
}

export default function Form() {
  const dispatch = useDispatch();
  const urlRef = new URLSearchParams(window.location.search).get("ref") || "";

  const [inputs, setInputs] = useState({
    fullName: "", email: "", mobile: "", address: "",
    nok: "", nokAddress: "", nokPhone: "", nokRelationship: "",
    password: "", password2: "",
    refCode: urlRef,
  });

  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setInputs(prev => {
      const next = { ...prev, [name]: value };
      if (touched[name]) {
        setErrors(errs => ({ ...errs, [name]: validate(name, value, next) }));
      }
      return next;
    });
  }, [touched]);

  const handleBlur = useCallback((e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validate(name, value, inputs) }));
  }, [inputs]);

  const validateAll = () => {
    const fields = ["fullName", "email", "mobile", "address", "nok", "nokRelationship", "nokAddress", "nokPhone", "password", "password2"];
    const newErrors = {};
    const newTouched = {};
    fields.forEach(f => {
      newTouched[f] = true;
      newErrors[f] = validate(f, inputs[f], inputs);
    });
    setTouched(newTouched);
    setErrors(newErrors);
    return Object.values(newErrors).every(e => !e);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agree) return Flash("warning", "Please agree to the terms of service", "", 3000);
    if (!validateAll()) return Flash("error", "Please fix the errors in the form", "", 3000);

    setSubmitting(true);
    dispatch(loadStart());
    try {
      const data = {
        ...inputs,
        email: inputs.email.trim().toLowerCase(),
        mobile: inputs.mobile.trim(),
        refCode: inputs.refCode ? inputs.refCode.trim().toUpperCase() : undefined,
      };
      delete data.password2;

      const response = await useAxios.post("/auth/agent/sign-up", data);
      dispatch(loadStop());
      setSubmitting(false);

      if (response.data.code === 401) return Flash("error", "An account with this email already exists", "", 4000);
      if (response.data.code === 201) {
        Flash("success", "Account created! Redirecting to login...", "", 3000);
        setTimeout(() => window.location.assign("/accounts?q=login"), 2000);
      }
    } catch (error) {
      dispatch(loadStop());
      setSubmitting(false);
      Flash("error", error.message || "Something went wrong", "", 3000);
    }
  };

  const strength = passwordStrength(inputs.password);

  const fieldProps = (name) => ({
    name,
    value: inputs[name],
    error: errors[name] || "",
    touched: !!touched[name],
    onChange: handleChange,
    onBlur: handleBlur,
  });

  return (
    <div style={s.page}>
      <style>{`
        .reg-input:focus { border-color: ${PRIMARY} !important; box-shadow: 0 0 0 3px rgba(167,204,72,0.15) !important; }
        .reg-select:focus { border-color: ${PRIMARY} !important; box-shadow: 0 0 0 3px rgba(167,204,72,0.15) !important; }
        .submit-btn:hover:not(:disabled) { filter: brightness(1.06); transform: translateY(-1px); }
        .submit-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .back-link:hover { color: ${PRIMARY} !important; }
        .agree-checkbox { width: 18px; height: 18px; accent-color: ${PRIMARY}; cursor: pointer; margin-right: 10px; flex-shrink: 0; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={s.card}>
        {/* Header */}
        <div style={s.formHeader}>
          <Link to="/" className="back-link" style={s.backLink}>
            <i className="bi bi-arrow-left" style={{ marginRight: 6 }} />Back
          </Link>
          <div style={s.formTitle}>Sales Agent Application</div>
          <p style={s.formSubtitle}>
            Fill in your details below. All fields marked <span style={s.required}>*</span> are required.
          </p>
          {urlRef && (
            <div style={s.refBanner}>
              <i className="bi bi-person-check-fill" style={{ color: PRIMARY, marginRight: 8 }} />
              Referred by code <strong style={{ color: PRIMARY, marginLeft: 4 }}>{urlRef.toUpperCase()}</strong>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {/* Bio Data */}
          <div style={s.section}>
            <SectionTitle icon="bi bi-person-lines-fill" title="Personal Information" subtitle="Your basic contact details" />
            <div style={s.grid2}>
              <Field label="Full Name" {...fieldProps("fullName")} />
              <Field label="Email Address" type="email" {...fieldProps("email")} />
              <Field label="Mobile Number" type="tel" maxLength={11} {...fieldProps("mobile")} hint="11-digit Nigerian number" />
              <Field label="Residential Address" {...fieldProps("address")} />
              <Field
                label="Referral Code"
                name="refCode"
                value={inputs.refCode}
                error=""
                touched={false}
                onChange={handleChange}
                onBlur={() => {}}
                hint="Optional — enter the code of whoever referred you"
                required={false}
              >
                <input
                  className="reg-input"
                  style={{
                    ...s.input,
                    ...(inputs.refCode && urlRef ? { background: "#f5fff0", borderColor: PRIMARY } : {}),
                  }}
                  name="refCode"
                  value={inputs.refCode}
                  onChange={handleChange}
                  autoComplete="off"
                  placeholder="e.g. ABCD1234"
                />
              </Field>
            </div>
          </div>

          {/* Next of Kin */}
          <div style={s.section}>
            <SectionTitle icon="bi bi-people-fill" title="Next of Kin" subtitle="Someone we can contact if needed" />
            <div style={s.grid2}>
              <Field label="Full Name" {...fieldProps("nok")} />
              <Field label="Relationship" {...fieldProps("nokRelationship")}>
                <select
                  className="reg-select"
                  style={{
                    ...s.input,
                    ...s.select,
                    ...(touched.nokRelationship && errors.nokRelationship ? s.inputError : {}),
                    ...(touched.nokRelationship && !errors.nokRelationship && inputs.nokRelationship ? s.inputOk : {}),
                  }}
                  name="nokRelationship"
                  value={inputs.nokRelationship}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="">Select relationship</option>
                  {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                {touched.nokRelationship && errors.nokRelationship && (
                  <span style={s.errorMsg}><i className="bi bi-exclamation-circle" style={{ marginRight: 4 }} />{errors.nokRelationship}</span>
                )}
              </Field>
              <Field label="Address" {...fieldProps("nokAddress")} />
              <Field label="Phone Number" type="tel" maxLength={11} {...fieldProps("nokPhone")} />
            </div>
          </div>

          {/* Password */}
          <div style={s.section}>
            <SectionTitle icon="bi bi-shield-lock-fill" title="Create a Password" subtitle="Use at least 8 characters" />
            <div style={s.grid2}>
              <div>
                <PasswordField label="Password" {...fieldProps("password")} />
                {inputs.password && (
                  <div style={s.strengthWrap}>
                    <div style={s.strengthBar}>
                      {[1, 2, 3, 4, 5].map(i => (
                        <div
                          key={i}
                          style={{
                            ...s.strengthSeg,
                            background: i <= strength ? strengthColor[strength] : "#e8e8e8",
                            transition: "background 0.3s",
                          }}
                        />
                      ))}
                    </div>
                    <span style={{ ...s.strengthLabel, color: strengthColor[strength] }}>
                      {strengthLabel[strength]}
                    </span>
                  </div>
                )}
              </div>
              <PasswordField label="Confirm Password" {...fieldProps("password2")} />
            </div>
          </div>

          {/* Agreement + Submit */}
          <div style={s.section}>
            <div style={s.agreeRow}>
              <input
                type="checkbox"
                className="agree-checkbox"
                id="agree"
                checked={agree}
                onChange={e => setAgree(e.target.checked)}
              />
              <label htmlFor="agree" style={s.agreeLabel}>
                I have read and agree to the{" "}
                <a href="/terms_conditions" style={s.agreeLink} target="_blank" rel="noreferrer">Terms and Conditions</a>
                {" "}and{" "}
                <a href="/privacy" style={s.agreeLink} target="_blank" rel="noreferrer">Privacy Policy</a>.
              </label>
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={!agree || submitting}
              style={{ ...s.submitBtn, transition: "filter 0.2s, transform 0.2s" }}
            >
              {submitting ? (
                <>
                  <span style={s.spinner} />
                  Submitting...
                </>
              ) : (
                <>
                  <i className="bi bi-send-fill" style={{ marginRight: 8 }} />
                  Submit Application
                </>
              )}
            </button>

            <p style={s.loginPrompt}>
              Already have an account?{" "}
              <Link to="/accounts?q=login" style={s.agreeLink}>Log in here</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: "100vh",
    background: "#f4f5f7",
    padding: "40px 0 60px",
  },
  card: {
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 2px 24px rgba(0,0,0,0.08)",
    maxWidth: 760,
    margin: "0 auto",
    overflow: "hidden",
  },

  formHeader: {
    background: DARK,
    padding: "32px 36px 28px",
    borderBottom: "3px solid " + PRIMARY,
  },
  backLink: {
    display: "inline-flex", alignItems: "center",
    color: "rgba(255,255,255,0.45)", textDecoration: "none",
    fontSize: 13, marginBottom: 16, transition: "color 0.2s",
  },
  formTitle: {
    fontSize: 24, fontWeight: 800, color: "#fff",
    letterSpacing: "-0.3px", marginBottom: 6,
  },
  formSubtitle: {
    fontSize: 14, color: "rgba(255,255,255,0.5)",
    margin: 0, lineHeight: 1.5,
  },
  refBanner: {
    display: "inline-flex", alignItems: "center",
    background: "rgba(167,204,72,0.12)",
    border: "1px solid rgba(167,204,72,0.25)",
    borderRadius: 8, padding: "7px 14px",
    fontSize: 13, color: "rgba(255,255,255,0.8)",
    marginTop: 14,
  },

  section: {
    padding: "28px 36px",
    borderBottom: "1px solid #f0f0f0",
  },
  sectionTitle: {
    display: "flex", alignItems: "flex-start", gap: 12,
    marginBottom: 22,
  },
  sectionIcon: {
    width: 36, height: 36, borderRadius: 9,
    background: "rgba(167,204,72,0.1)",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, marginTop: 1,
  },
  sectionTitleText: {
    fontSize: 15, fontWeight: 700, color: DARK,
  },
  sectionSubtitle: {
    fontSize: 12, color: "#999", marginTop: 2,
  },

  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: "16px 20px",
  },

  fieldWrap: { display: "flex", flexDirection: "column", gap: 5 },
  label: {
    fontSize: 12, fontWeight: 600, color: "#555",
    textTransform: "uppercase", letterSpacing: 0.5,
    display: "flex", alignItems: "center", gap: 3,
  },
  required: { color: ERROR, fontSize: 13 },
  input: {
    border: "1.5px solid #e0e0e0", borderRadius: 9,
    padding: "10px 13px", fontSize: 14, outline: "none",
    width: "100%", boxSizing: "border-box", color: DARK,
    background: "#fff", transition: "border-color 0.2s, box-shadow 0.2s",
    fontFamily: "inherit",
  },
  inputError: {
    borderColor: ERROR,
    boxShadow: "0 0 0 3px rgba(229,57,53,0.1)",
  },
  inputOk: {
    borderColor: "#a3d977",
  },
  select: {
    cursor: "pointer", appearance: "none",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23999' d='M1 1l5 5 5-5'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
    paddingRight: 36,
  },
  errorMsg: {
    fontSize: 11, color: ERROR,
    display: "inline-flex", alignItems: "center",
  },
  hint: { fontSize: 11, color: "#aaa" },

  pwdRow: { position: "relative" },
  eyeBtn: {
    position: "absolute", right: 12, top: "50%",
    transform: "translateY(-50%)",
    background: "none", border: "none",
    cursor: "pointer", color: "#aaa", fontSize: 16, padding: 2,
    lineHeight: 1,
  },

  strengthWrap: {
    display: "flex", alignItems: "center", gap: 10, marginTop: 7,
  },
  strengthBar: { display: "flex", gap: 4, flex: 1 },
  strengthSeg: {
    height: 4, flex: 1, borderRadius: 4,
  },
  strengthLabel: { fontSize: 11, fontWeight: 600, minWidth: 60 },

  agreeRow: {
    display: "flex", alignItems: "flex-start",
    marginBottom: 24,
  },
  agreeLabel: {
    fontSize: 13, color: "#555", lineHeight: 1.55, cursor: "pointer",
  },
  agreeLink: { color: PRIMARY, textDecoration: "none", fontWeight: 600 },

  submitBtn: {
    background: PRIMARY, color: DARK, border: "none",
    borderRadius: 10, padding: "13px 32px",
    fontSize: 15, fontWeight: 700, cursor: "pointer",
    display: "inline-flex", alignItems: "center",
    boxShadow: "0 4px 16px rgba(167,204,72,0.3)",
    width: "100%", justifyContent: "center",
  },
  spinner: {
    width: 16, height: 16, borderRadius: "50%",
    border: "2px solid rgba(44,45,63,0.2)",
    borderTopColor: DARK,
    marginRight: 8,
    display: "inline-block",
    animation: "spin 0.6s linear infinite",
  },

  loginPrompt: {
    textAlign: "center", fontSize: 13,
    color: "#888", marginTop: 16, marginBottom: 0,
  },
};
